import { useId } from 'react';
import type { CheckBatch } from '../types';
import { NOT_AVAILABLE, formatDate, formatDateTime, formatMoney, plural } from '../format';
import { Modal } from './Modal';

interface DraftDialogProps {
  batch: CheckBatch;
  /** `saved`: just saved from Review (Step 3 layout). `existing`: a saved draft was found when the page opened (Step 1 layout). */
  variant: 'saved' | 'existing';
  busy: boolean;
  onBackToDashboard: () => void;
  onDiscard: () => void;
  onContinue: () => void;
}

/** Up to two check numbers, then "+n more"; the full list is in the table behind the dialog. */
function summarizeCheckNumbers(batch: CheckBatch): string {
  const numbers = batch.lines.filter((l) => l.included).map((l) => l.checkNumber);
  if (numbers.length === 0) return NOT_AVAILABLE;
  if (numbers.length <= 2) return numbers.join(', ');
  return `${numbers.slice(0, 2).join(', ')} +${numbers.length - 2} more`;
}

function Card({ label, children, wide, testId }: { label: string; children: React.ReactNode; wide?: boolean; testId?: string }) {
  return (
    <div className={wide ? 'wc-draft__card wc-draft__card--wide' : 'wc-draft__card'}>
      <dt>{label}</dt>
      <dd data-testid={testId}>{children}</dd>
    </div>
  );
}

function DraftPill() {
  return <span className="wc-draft__pill"><span className="wc-draft__dot" aria-hidden="true" />Draft</span>;
}

/**
 * S10 (Step 3) / S13 (Step 1). One dialog, two card layouts, same content: shows what the saved draft holds so the admin can continue,
 * discard (confirmed separately) or leave. Debit Date is not provided by the server yet (BACKEND GAP) and is shown as an em dash.
 */
export function DraftDialog({ batch, variant, busy, onBackToDashboard, onDiscard, onContinue }: DraftDialogProps) {
  const titleId = useId();
  const bodyId = useId();
  const reference = `CB-${batch.id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
  const employees = plural(batch.totals.employeesIncluded, 'Employee', 'Employees');
  const method = 'Write Check';
  const checkNumbers = summarizeCheckNumbers(batch);
  return (
    <Modal titleId={titleId} describedById={bodyId} onClose={onContinue} busy={busy} closeLabel="Close draft summary">
      <div className="wc-draft" data-layout={variant === 'saved' ? 'step3' : 'step1'}>
        <svg className="wc-draft__icon" viewBox="0 0 96 96" aria-hidden="true" focusable="false">
          <path fill="currentColor" fillRule="evenodd" d="M18 12h46l14 14v52a6 6 0 0 1-6 6H18a6 6 0 0 1-6-6V18a6 6 0 0 1 6-6Zm10 4v18a3 3 0 0 0 3 3h28a3 3 0 0 0 3-3V19l-3-3H28Zm20 28a13 13 0 1 0 0 26 13 13 0 0 0 0-26Z" />
        </svg>
        <h3 id={titleId} className="wc-draft__title">Payment Batch Saved as Draft</h3>
        <p id={bodyId} className="wc-draft__text">Your payment batch has been saved. You can return to submit it at any time before the pay date.</p>
        <p className="wc-draft__ref" data-testid="draft-batch-id">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
            <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 6l1.5 1.5L8 5M4 12l1.5 1.5L8 11M4 18l1.5 1.5L8 17M12 6h8M12 12h8M12 18h8" />
          </svg>
          Batch ID: {reference}
        </p>
        <dl className="wc-draft__grid">
          {variant === 'saved' ? (
            <>
              <Card label="Pay Date">{formatDate(batch.payDate)}</Card>
              <Card label="Payment Method">{method}</Card>
              <Card label="Employees to be paid">{employees}</Card>
              <Card label="Total Amount"><span className="wc-accent">{formatMoney(batch.totals.checkTotal)}</span></Card>
              <Card label="Debit Date">{NOT_AVAILABLE}</Card>
              <Card label="Current Status"><DraftPill /></Card>
            </>
          ) : (
            <>
              <Card label="Payment Method">{method}</Card>
              <Card label="Pay Date">{formatDate(batch.payDate)}</Card>
              <Card label="Employees to be paid">{employees}</Card>
              <Card label="Debit Date">{NOT_AVAILABLE}</Card>
              <div className="wc-draft__card wc-draft__card--amount">
                <div>
                  <dt>Total Amount</dt>
                  <dd><span className="wc-accent">{formatMoney(batch.totals.checkTotal)}</span></dd>
                </div>
                <DraftPill />
              </div>
              <Card label="Check Number" testId="draft-check-numbers">{checkNumbers}</Card>
            </>
          )}
          <div className="wc-draft__card wc-draft__card--wide wc-draft__card--split">
            <div><dt>Draft By</dt><dd data-testid="draft-by">{batch.lastSavedByEmail ?? NOT_AVAILABLE}</dd></div>
            <div className="wc-draft__right"><dt>Last Saved</dt><dd data-testid="draft-last-saved">{batch.lastSavedAtUtc ? formatDateTime(batch.lastSavedAtUtc) : NOT_AVAILABLE}</dd></div>
          </div>
        </dl>
        <div className="wc-draft__actions">
          <button type="button" className="wc-btn wc-btn--outline wc-btn--lg" onClick={onBackToDashboard} disabled={busy}>Back to Admin Dashboard</button>
          <span>
            <button type="button" className="wc-link wc-link--muted" onClick={onDiscard} disabled={busy}>Discard Draft</button>
            <button type="button" className="wc-btn wc-btn--green wc-btn--lg" onClick={onContinue} disabled={busy} data-autofocus>Continue Editing</button>
          </span>
        </div>
      </div>
    </Modal>
  );
}
